import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * @internal Hook genérico que persiste estado no localStorage, chaveado pela rota atual.
 *
 * Uso:
 *   const [viewMode, setViewMode] = usePersistedState<ViewMode>('viewMode', 'lista');
 *   // Salva em localStorage como "ac:viewMode:/comercial/oportunidades" = "kanban"
 *
 * @param key   Nome da chave (ex: 'viewMode', 'sortOrder', 'pageSize')
 * @param defaultValue  Valor padrão se não existir no storage
 * @param routeKey  Opcional — override da rota (por padrão usa useLocation().pathname)
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
  routeKey?: string,
): [T, (value: T | ((prev: T) => T)) => void] {
  const { pathname } = useLocation();
  const storageKey = `ac:${key}:${routeKey || pathname}`;

  const [state, setStateRaw] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) {
        return JSON.parse(stored) as T;
      }
    } catch {
      // ignore parse errors
    }
    return defaultValue;
  });

  const setState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStateRaw((prev) => {
        const next = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // ignore quota errors
        }
        return next;
      });
    },
    [storageKey],
  );

  return [state, setState];
}
